/**
 * AppShell - the chrome around every module route + the auth gate.
 *
 * Header carries the brand + module switcher + an OPTIONAL `headerSlot`
 * filled by the composition root + a sign-in/out button driven by
 * `useAuth`. The body is `<Outlet />` once authenticated; a login
 * splash + button otherwise.
 *
 * Auth states (driven by AuthProvider context):
 *   initializing  -> small full-page "Connecting..." splash. Brief.
 *   unauthenticated -> branded splash + "Sign in" button. Clicking
 *                      hands off to Keycloak; on return the
 *                      redirectUri brings the user back here +
 *                      AuthProvider flips status to authenticated.
 *   authenticated  -> full chrome + <Outlet />.
 *
 * Why a slot: the position-queue widget reads framework data (CAP
 * queue counts) and so must live in framework/. The P0.8 module-
 * boundary rule forbids shared/ importing from feature modules - so
 * App.tsx (the composition root, where ALL modules are imported)
 * constructs the widget + hands it in.
 *
 * RTL discipline: logical Tailwind utilities only (ps-/pe-/ms-/me-/
 * start-/end-). Directional classes (left-/right-/ml-/mr-/pl-/pr-)
 * are ESLint errors - v2.1 RTL ship is a translate, not a rewrite.
 */
import { useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useScrollToHash } from "shared/hooks/useScrollToHash";
import { useTranslation } from "react-i18next";

import { useAuth } from "shared/auth/AuthProvider";
import { useBankIdentity } from "shared/api/hooks/useBankIdentity";
import { brokeredBy, changePassword, tokenPayload } from "shared/auth/keycloakAdapter";
import { accountConsoleUrl } from "shared/auth/identityLinks";
import { Button } from "shared/components/Button";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { LanguageToggle } from "shared/i18n/LanguageToggle";
import { NotificationBell } from "shared/notifications/NotificationBell";
import { LicenseGate } from "shared/licensing/LicenseGate";
import { ModuleNotLicensed } from "shared/licensing/ModuleNotLicensed";
import { ModuleNotYours } from "shared/licensing/ModuleNotYours";
import {
  featureModuleForPath,
  isModuleLicensed,
  useLicenseStatus,
} from "shared/licensing/useLicense";
import { ModuleBreadcrumb } from "shared/layout/ModuleBreadcrumb";
import { ModuleTabs } from "shared/layout/ModuleTabs";
import { QuestionLinks, QuestionTabs } from "shared/layout/QuestionNav";
import { TopnavSearch } from "shared/search/TopnavSearch";
import type { ApiError } from "shared/api/client";
import { useMe } from "shared/api/useMe";
import { hasModule } from "shared/api/authority";
import { setsTheBankUp } from "shared/onboarding/audience";
import { PseudoLocaleBanner } from "shared/i18n/PseudoLocaleBanner";
import { ExaminerWorkspace } from "shared/examiner/ExaminerWorkspace";
import { useOnboardingStatus } from "shared/api/useOnboarding";

/* Round 16 H19, tenth round -- the wordmark is a mark, not copy. Read
   through the catalogue, the pseudo locale transliterated "MARSAD" into
   Arabic script and padded the Arabic half with its own filler word, so
   the lockup carried the Arabic name twice. A real Arabic catalogue
   would have wrapped both halves as untranslated. The lockup is the same
   in every language; the catalogue keeps app.title for prose and the
   document title. */
const BRAND_LATIN = "MARSAD";
const BRAND_ARABIC = "\u0645\u0631\u0635\u062f";

interface AppShellProps {
  /** Module-level slot mounted via App.tsx (`<PositionQueueWidget />`).
   * Originally sat in the top header bar; relocated into the secondary
   * band (beside the breadcrumb/tabs) once the bar got too crowded --
   * the P127 language toggle pushed the bar into horizontal overflow. */
  headerSlot?: ReactNode;
  /** P214.4 (Observatory): the night-sky hero, rendered full-bleed navy
   *  flush BENEATH the header (one continuous sky) -- but only on the
   *  Home route ("/"). Other routes keep the navy header + daylight body.
   *  App.tsx fills it with `<HomeHero />` (twin, reads board + cascade). */
  heroSlot?: ReactNode;
}

// P214.2 (Observatory): active state is a brass underline, not a filled
// chip. Inactive nav reads in sky.nav; hover lifts to cream.
const NAV_LINK_CLASS = [
  "inline-flex items-center gap-1.5 px-3 py-[7px] rounded-md text-[12.5px] font-medium",
  "text-sky-nav hover:text-brand-cream",
  "aria-[current=page]:text-brand-brass aria-[current=page]:rounded-none",
  "aria-[current=page]:shadow-[inset_0_-2px_0_theme(colors.brand.brass)]",
].join(" ");

// Inline SVG (not an <img src=...>) so currentColor inherits from the
// parent's text-brand-brass class. The crown circle at (55,12) stays
// hard-coded brass even in dark contexts -- v1 convention; the apex
// node is the brand mark within the mark.
// Source: frontend/public/marsad-glyph.svg (used as favicon).
function BrandGlyph({ className = "h-7 w-7" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={`${className} text-brand-brass shrink-0`}
      role="img"
      aria-label="MARSAD"
    >
      <polyline
        points="8,85 22,30 40,55 55,12 70,55 85,30 95,85"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.55"
      />
      <line
        x1="22"
        y1="30"
        x2="85"
        y2="30"
        stroke="currentColor"
        strokeWidth="0.7"
        opacity="0.35"
        strokeDasharray="2 3"
      />
      <circle cx="8" cy="85" r="3" fill="currentColor" />
      <circle cx="22" cy="30" r="4.4" fill="currentColor" />
      <circle cx="40" cy="55" r="3" fill="currentColor" />
      <circle cx="55" cy="12" r="6" fill="#D9BC6C" />
      <circle cx="70" cy="55" r="3" fill="currentColor" />
      <circle cx="85" cy="30" r="4.4" fill="currentColor" />
      <circle cx="95" cy="85" r="3" fill="currentColor" />
    </svg>
  );
}

// ── Auth splash screens ─────────────────────────────────────────────

function InitializingSplash() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="text-slate-600 text-sm">{t("auth.initializing")}</div>
    </div>
  );
}

function AuthErrorScreen({ error }: { error: Error | null }) {
  const { t } = useTranslation();
  const message = error?.message ?? "Unknown error";
  const onRetry = () => {
    // Strip the OIDC callback fragment + retry. The hash sticks around
    // after a failed code-exchange and re-running init against a
    // consumed code just fails again.
    window.location.replace(window.location.origin);
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-6">
      <div className="max-w-xl w-full bg-white border border-red-200 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-red-700 mb-2">
          {t("auth.errorTitle")}
        </h2>
        <p className="text-sm text-slate-700 mb-4">{t("auth.errorBody")}</p>
        <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-auto mb-4">
          {message}
        </pre>
        <button
          type="button"
          onClick={onRetry}
          className="px-4 py-2 rounded-md text-sm font-medium bg-slate-900 text-white hover:bg-slate-700"
        >
          {t("auth.errorRetry")}
        </button>
      </div>
    </div>
  );
}

/**
 * P390: the profile wait is NOT the auth wait, and must not claim to be.
 *
 * Keycloak succeeding and `/v1/me` failing are different faults in
 * different systems, but both used to render `auth.initializing`
 * ("Connecting to identity provider..."). That message sends whoever is
 * debugging to Keycloak, which is fine, and healthy, and not the
 * problem. It cost two diagnostic detours before anyone suspected the
 * MARSAD API.
 */
function ProfileLoadingSplash() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="text-slate-600 text-sm">{t("auth.loadingProfile")}</div>
    </div>
  );
}

/**
 * P390: `/v1/me` failing used to have NO branch at all.
 *
 * The splash above it is deliberate - the comment on the examiner check
 * says to hold until /me settles so neither shell flashes the wrong
 * chrome. But nothing handled /me never settling, so the hold became
 * indefinite and silent. This completes that intent rather than
 * overriding it: still no wrong shell, but now the user is told why and
 * can act.
 */
function ProfileErrorScreen({
  error,
  onRetry,
  onSignOut,
}: {
  error: ApiError | null;
  onRetry: () => void;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();
  const rateLimited = error?.status === 429;
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-6">
      <div className="max-w-xl w-full bg-white border border-amber-200 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-amber-800 mb-2">
          {t("auth.profileErrorTitle")}
        </h2>
        <p className="text-sm text-slate-700 mb-4">
          {rateLimited ? t("auth.profileErrorRateLimited") : t("auth.profileErrorBody")}
        </p>
        <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-auto mb-4">
          {error?.message ?? t("screens.appShell.unknownError")}
        </pre>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="profile-error-retry"
            onClick={onRetry}
            className="px-4 py-2 rounded-md text-sm font-medium bg-slate-900 text-white hover:bg-slate-700"
          >
            {t("auth.profileErrorRetry")}
          </button>
          <button
            type="button"
            data-testid="profile-error-signout"
            onClick={onSignOut}
            className="px-4 py-2 rounded-md text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            {t("auth.logout")}
          </button>
        </div>
      </div>
    </div>
  );
}

// Exported for H9's test: the stalled-redirect path is a timer and
// two pieces of state, and driving it through the whole shell would
// test the mock rather than the behaviour.
export function LoginGate() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stalled, setStalled] = useState(false);

  const onClick = () => {
    setLoading(true);
    setStalled(false);
    // login() returns a promise that never resolves (the browser
    // navigates to Keycloak), so a redirect that FAILS -- ERR_FAILED,
    // seen five times across the walkthrough -- neither resolves nor
    // rejects. The button stayed disabled and the splash sat there
    // forever with no way back.
    //
    // H9: if we are still here after a few seconds the navigation did
    // not happen, so say so and offer the retry. The timer is
    // harmless when the redirect works, because the page is gone
    // before it fires.
    const timer = setTimeout(() => {
      setStalled(true);
      setLoading(false);
    }, 6000);
    void login().catch(() => {
      clearTimeout(timer);
      setStalled(true);
      setLoading(false);
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-navy text-brand-cream">
      <div className="max-w-xl text-center px-6">
        {/* Marketing lockup: glyph + MARSAD (display) + مرصد (Amiri,
            brass). The Arabic wordmark is sized larger here than in
            the persistent header to read as a deliberate brand
            statement on the splash. */}
        <div className="flex items-center justify-center gap-4 mb-3">
          <BrandGlyph className="h-14 w-14" />
          <h1
            data-testid="login-wordmark"
            className="font-display text-5xl font-semibold tracking-wide text-brand-cream"
          >
            {BRAND_LATIN}
          </h1>
          <span aria-hidden="true" className="font-arabic text-4xl text-brand-brass">
            {BRAND_ARABIC}
          </span>
        </div>
        {/* Strapline in mono with wide tracking -- the brand voice. */}
        <p className="font-mono uppercase tracking-[0.25em] text-xs text-brand-cream/70 mb-8">
          {t("app.strapline")}
        </p>
        {/* Category claim is the heading; "Sign in to continue" sits
            below as the action prompt. */}
        <h2 className="font-display text-2xl font-medium mb-4 text-brand-cream">
          {t("app.heroClaim")}
        </h2>
        <p className="text-sm text-brand-cream/70 mb-6 max-w-md mx-auto">
          {t("auth.loginRequiredBody")}
        </p>
        {/* P214.7 (Observatory): the splash CTA reuses the shared Button
            primaryOnDark variant (brass on navy) instead of a bespoke
            brass button -- one button treatment across the app. */}
        <Button
          variant="primaryOnDark"
          disabled={loading}
          onClick={onClick}
          className="px-5 py-2"
        >
          {stalled ? t("auth.loginRetry") : t("auth.login")}
        </Button>
        {stalled && (
          <p data-testid="login-stalled" className="mt-3 text-sm text-brand-cream/80">
            {t("auth.loginStalled")}
          </p>
        )}
      </div>
    </div>
  );
}

// ── User menu (signed-in chip + sign-out) ───────────────────────────

// Exported for the change-password test.
export function UserChip() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  // tokenPayload reads keycloak's parsed JWT claims directly. We could
  // route this through useMe() once auth is live + that endpoint
  // works against the JWT, but for now the JWT username claim is
  // available immediately without an extra round-trip.
  const payload = tokenPayload();
  const username =
    typeof payload?.preferred_username === "string"
      ? (payload.preferred_username as string)
      : "user";
  const accountUrl = accountConsoleUrl();
  // Signed in through the organisation's own provider: the password is
  // theirs to change there, not here.
  const viaProvider = brokeredBy(payload);

  return (
    <div className="flex items-center gap-3 text-xs text-sky-text shrink-0">
      <NotificationBell />
      <LanguageToggle />
      {/* Username is supplementary -- hide it before the actionable
          controls (bell / language / sign-out) on tighter widths. */}
      <span
        className="hidden xl:inline text-sky-text"
        title={t("auth.signedInAs", { username })}
      >
        {username}
      </span>
      {/* Where a person changes their own password. It was linked from
          nowhere, so an administrator went looking in the Users page and
          followed its console link onto the hosted edge's refusal. */}
      {!viaProvider && (
        <button
          type="button"
          data-testid="user-change-password"
          onClick={() => void changePassword()}
          title={t("auth.changePasswordTitle")}
          className="border-b border-brand-navyLine pb-px text-sky-nav hover:text-brand-cream"
        >
          {t("auth.changePassword")}
        </button>
      )}
      {accountUrl && (
        <a
          href={accountUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="user-account-link"
          title={t("auth.yourAccountTitle")}
          className="border-b border-brand-navyLine pb-px text-sky-nav hover:text-brand-cream"
        >
          {t("auth.yourAccount")}
          {/* Round 17 K20 -- say that it opens a new tab. */}
          <span aria-hidden="true"> ↗</span>
          <span className="sr-only"> {t("auth.opensNewTab")}</span>
        </a>
      )}
      <button
        type="button"
        onClick={() => void logout()}
        className="border-b border-brand-navyLine pb-px text-sky-nav hover:text-brand-cream"
      >
        {t("auth.logout")}
      </button>
    </div>
  );
}

// ── AppShell ────────────────────────────────────────────────────────

export function AppShell({ headerSlot, heroSlot }: AppShellProps) {
  const { t } = useTranslation();
  const { status, error, logout } = useAuth();
  // Hooks run on every render including the initializing/unauthenticated
  // branches below -- gate the queries so they don't fire token-less
  // during keycloak init (they 401-retried until auth settled).
  const me = useMe(status === "authenticated");
  const isAdmin = me.data?.is_admin ?? false;
  // P270 follow-on: hide a feature module's nav link when the deployment
  // license doesn't include it (fails open -> no change unless the gate is
  // on AND the license is scoped).
  // D11 -- an examiner's token is confined to /v1/examiner/* by the
  // boundary middleware, so every operator-shell query it fires can
  // only 403 -- for a shell they never see, since the branch below
  // hands them the workspace instead. Hooks cannot be conditional, so
  // the condition goes where it belongs: on the query.
  const isExaminer = me.data?.realm_roles?.includes("examiner") ?? false;
  // Round 16 H30 -- and not before /v1/me has said who is looking: the
  // two queries below fired while it was in flight, so an examiner's
  // every load still carried their two 403s.
  const wantsOperatorShell = status === "authenticated" && !!me.data && !isExaminer;
  // Primed here for the operator shell; QuestionLinks reads it.
  useLicenseStatus(wantsOperatorShell);
  const onboarding = useOnboardingStatus(wantsOperatorShell);
  // D10 -- the setup ladder sat in EVERYBODY's navigation: the
  // preparer's, the owner's, the reviewer's, the CRO's. It is a list of
  // stations belonging to two seats, and offering it to the other four
  // put a permanent "finish setting up the bank" in the way of people
  // who cannot do a single step on it.
  const showGettingStarted =
    (onboarding.data?.show_getting_started ?? false) && setsTheBankUp(me.data);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  // Round 17 R3 -- a step that points into a page lands on its part.
  useScrollToHash();

  if (status === "initializing") {
    return <InitializingSplash />;
  }
  if (status === "error") {
    return <AuthErrorScreen error={error} />;
  }
  if (status === "unauthenticated") {
    return <LoginGate />;
  }
  // P358: an examiner-role account gets the curated workspace INSTEAD of
  // the operator shell -- zero operator chrome, no operator API calls
  // (the backend boundary confines the token anyway; this is the honest
  // UI for it). Hold on the splash until /me settles so neither branch
  // flashes the wrong shell.
  if (me.isLoading) {
    return <ProfileLoadingSplash />;
  }
  if (me.isError) {
    return (
      <ProfileErrorScreen
        error={me.error}
        onRetry={() => void me.refetch()}
        onSignOut={logout}
      />
    );
  }
  if (isExaminer) {
    return <ExaminerWorkspace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-paper text-slate-900">
      {/* Says on screen that the pseudo-locale is not Arabic. Dev
          builds only; the branch is dead code in a production bundle. */}
      <PseudoLocaleBanner />
      {/* Round 12 H3 -- the one structural accessibility gap the first
          measurement of this product found, alongside four unnamed
          fields. Every screen has one h1, 501 controls carry a name,
          forty consecutive Tab presses land on a real control with a
          visible focus ring -- and there was no way past the
          navigation, so a keyboard user crossed the whole of it on
          every route change. Visually hidden until focused, which is
          the convention a keyboard user already expects. */}
      <a
        href="#main"
        data-testid="skip-to-main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded focus:bg-brand-brass focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-brand-navy"
      >
        {t("app.skipToContent")}
      </a>
      <header className="bg-brand-navy text-white border-b border-brand-navyLine">
        {/* G3: this row did not wrap, so at 1280 it overflowed the
            viewport -- 31px in English, 141px in Arabic, where the
            longer strings bite harder. `min-w-0` on the search box
            traded that for a search box collapsed to 50px, which is
            not a fix, only a choice of symptom.
            Wrapping solves both, and survives string lengths nobody
            knows yet: the real Arabic catalogue does not exist. */}
        {/* ROUND 13 G3 -- and on a phone the row did not merely
            overflow, it dragged every page sideways with it. Measured
            at 375px on the built bundle: the module nav alone is 829px
            wide and `shrink-0`, so the document scrolled 494px on
            every route and 660px on Home. 40px of padding each side on
            a 375px viewport is a third of the screen before anything
            is drawn. */}
        <div className="mx-auto max-w-[1260px] px-4 sm:px-10 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* P79.2 (brand handoff): header lockup is glyph + MARSAD
              (display) + Arabic مرصد (Amiri, brass, ~0.55x Latin).
              The feature-description subtitle was REMOVED -- that
              line is the category claim + belongs in Home's hero
              heading, not as a logo subtitle. */}
          <NavLink to="/" className="flex items-center gap-3 shrink-0">
            <BrandGlyph />
            <span
              data-testid="app-wordmark"
              className="font-display text-lg font-semibold tracking-wide"
            >
              {BRAND_LATIN}
            </span>
            <span
              aria-hidden="true"
              className="font-arabic text-base text-brand-brass leading-none hidden sm:inline"
            >
              {BRAND_ARABIC}
            </span>
          </NavLink>
          {/* G3: the nav keeps every destination and scrolls INSIDE
              itself below `sm` -- the standing rule is that wide
              content scrolls in its own container and the page body
              never scrolls sideways. Above `sm` nothing changes: the
              round-3 decision that the nav holds its width and the
              search box has a floor is untouched. */}
          <nav
            className="flex items-center gap-1 min-w-0 max-w-full overflow-x-auto sm:overflow-x-visible shrink-0"
            aria-label="Main"
          >
            {showGettingStarted && (
              <NavLink
                to="/getting-started"
                data-testid="nav-getting-started-link"
                className={NAV_LINK_CLASS}
                end
              >
                {t("nav.gettingStarted")}
              </NavLink>
            )}
            {/* Navigation re-cut, option A (docs/the-story.md, section 3):
                the masthead names the five questions a person arrives
                with; the modules answer beneath them. Admin stays a
                utility link. */}
            <QuestionLinks className={NAV_LINK_CLASS} />
            {isAdmin && (
              <NavLink
                to="/admin"
                data-testid="nav-admin-link"
                className={NAV_LINK_CLASS}
                end
              >
                <MarsadIcon name="console" size={15} />
                {t("nav.admin")}
              </NavLink>
            )}
          </nav>
          {/* Search takes the slack between nav and the utility cluster
              and shrinks (min-w-0) before it can push the bell /
              language / sign-out controls off the bar -- the overcrowding
              that the position-queue widget used to cause here. The
              queue widget now lives in the secondary band below. */}
          {/* G3: `min-w-0` let this collapse to 50px at 1440, so the
              placeholder was clipped in BOTH languages -- the
              walkthrough reported it as an Arabic problem and it was
              never one. A search box narrower than its own prompt is
              not a search box, so it now has a floor and the nav
              yields instead. */}
          <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[13rem] flex justify-end">
            <TopnavSearch />
          </div>
          <UserChip />
        </div>
      </header>

      {/* P214.4: the continuous night sky -- a full-bleed navy band flush
          beneath the header (no seam), only on Home. The hero owns its
          own inner container + decorative star/constellation layers. */}
      {isHome && heroSlot ? (
        <div
          data-testid="appshell-hero"
          className="bg-brand-navy text-sky-text relative overflow-hidden"
        >
          {heroSlot}
        </div>
      ) : null}

      {/* P270.4: deployment-license gate. Invisible unless enforcement is
          on AND the license is unhealthy (default OFF -> renders null). */}
      <LicenseGate />

      <main id="main" className="flex-1" tabIndex={-1}>
        <AppShellBody headerSlot={headerSlot} />
      </main>

      {/* ROUND 13 H -- no screen carried a `footer` landmark.
          
          Assistive technology navigates a page by its landmarks, and
          this product had a banner, a main and a navigation and then
          stopped: there was nowhere to jump to for the things that sit
          at the end of a document. It is also the natural home for the
          one fact a reader holding a screenshot cannot otherwise
          recover -- which product drew it, and which build. */}
      <AppFooter />
    </div>
  );
}

const DOCS_URL: string = (import.meta.env.VITE_DOCS_URL as string | undefined) ?? "";

function AppFooter() {
  const { t } = useTranslation();
  const bank = useBankIdentity();
  return (
    <footer
      data-testid="app-footer"
      className="border-t border-paper-2 bg-paper px-4 py-4 text-[11px] text-slate-500 sm:px-10"
    >
      <div className="mx-auto flex max-w-[1260px] flex-wrap items-center gap-x-4 gap-y-1">
        <span>
          {t("app.footer.product", {
            product: t("app.title"),
            bank: bank.data?.display_name ?? t("app.title"),
          })}
        </span>
        {/* Round 14 F4 -- only where there IS documentation to open.
            "/docs/" is routed to the backend's API explorer, a developer
            tool that loads its scripts from a public CDN and renders
            blank on a network without one. The reader's documentation
            site is published separately; where a deployment has one it
            names it in VITE_DOCS_URL, and where it does not the link is
            not offered rather than pointing at the wrong thing. */}
        {DOCS_URL && (
          <a
            href={DOCS_URL}
            className="text-brand-brassDark hover:underline"
            data-testid="app-footer-docs"
          >
            {t("app.footer.docs")}
          </a>
        )}
      </div>
    </footer>
  );
}

// Some routes need the full viewport width: the KSA capability map
// (/twin/capmap, command-centre wall view) and the constellation
// (/twin/constellation, P83 SVG canvas where the graph IS the main
// event -- letting it letterbox in a max-w-7xl wrapper was the
// P87-followup complaint). They opt out of the max-w-7xl wrapper
// every other route sits inside; the route itself owns its padding
// + breadcrumb chrome.
const FULL_WIDTH_PATHS = new Set<string>(["/twin/capmap", "/twin/constellation"]);

function AppShellBody({ headerSlot }: { headerSlot?: ReactNode }) {
  const { pathname } = useLocation();
  const me = useMe();
  const isFullWidth = FULL_WIDTH_PATHS.has(pathname);
  // P270 follow-on: a deep-link into a feature module the deployment isn't
  // licensed for shows the calm ModuleNotLicensed placeholder instead of a
  // page that would 403 on its data calls. Default-invisible (fails open).
  const lic = useLicenseStatus().data;
  const blockedModule = featureModuleForPath(pathname);
  /* Round 10 F5 -- two different refusals, and they are not the same
     sentence. The deployment not licensing a module is an upgrade ask
     answered by an account team; this account not holding one the bank
     DOES license is an access fact, and telling a service owner to
     contact his account team about a module his colleagues use every
     day sends him to the wrong person. */
  const body =
    blockedModule && !isModuleLicensed(lic, blockedModule) ? (
      <ModuleNotLicensed module={blockedModule} />
    ) : blockedModule && !hasModule(me.data, blockedModule) ? (
      <ModuleNotYours module={blockedModule} />
    ) : (
      <Outlet />
    );
  if (isFullWidth) {
    // Immersive command-centre routes own the full viewport: the
    // breadcrumb/tabs band is dropped so the canvas goes edge to edge.
    // The queue widget would otherwise vanish here too (it only lives
    // in the secondary band) -- so it's floated into a compact navy
    // card pinned bottom-start instead. Bottom-START specifically: the
    // constellation's Inspector owns bottom-END (P87.1), so they never
    // collide. The card carries its own navy background because the
    // widget's pills are styled for the navy header, not the canvas.
    //
    // P272.5 (defect 6): the constellation ALSO owns a bottom-start
    // scrubber/blast control row, which bottom-4 overlapped. Lift the
    // widget above it there (bottom-24). Round 18 G27 -- the capability
    // map has one too: its walk-through runs the full width of the
    // bottom edge, and the queue covered six of its steps in English
    // (round 15 moved it to the other corner in right-to-left only).
    return (
      <div data-testid="appshell-full-width">
        {body}
        {headerSlot ? (
          <FloatingQueue bottom={floatingQueueBottom(pathname)}>
            {headerSlot}
          </FloatingQueue>
        ) : null}
      </div>
    );
  }
  // P214.5: on Home the queue pills live in the daylight onboarding
  // ribbon (light tone, rendered by Home itself), so the band's
  // dark-tone headerSlot is suppressed there to avoid a double render.
  // Every other route keeps the dark queue widget in this band.
  const isHome = pathname === "/";
  return (
    <div className="mx-auto max-w-[1260px] px-4 sm:px-10 py-8">
      {/* The position-queue widget (headerSlot) moved off the top bar
          (P127 language toggle made it overflow) into this band, where
          it sits opposite the breadcrumb/tabs with room to breathe. It
          self-hides when the user holds no positions, so the row
          collapses to just the breadcrumb when there's nothing to show. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <ModuleBreadcrumb />
        </div>
        {headerSlot && !isHome ? (
          <div className="min-w-0 max-w-full sm:shrink-0">{headerSlot}</div>
        ) : null}
      </div>
      {/* E1 -- the tab strip used to share this row with the queue
          widget inside a min-w-0, so it got 765px of a 1260px
          container and six of the Twin module's twelve tabs were
          simply not reachable. It gets the full width now, and wraps
          rather than clipping. */}
      <QuestionTabs fallback={<ModuleTabs />} />
      {body}
    </div>
  );
}

/**
 * The floating queue card on a full-bleed route, with a way to get it
 * out of the way (round 10 A1).
 *
 * MEASURED: on the constellation it sits over three vendor nodes, and
 * a node the scene draws that cannot be clicked because the chrome is
 * on top of it is the same defect as a node that does not answer the
 * click -- it simply has a different cause. The card earns its place
 * (it is the only wayfinding on a route with no chrome), so it stays,
 * and it collapses to a small tab that the canvas can be read behind.
 *
 * The choice persists, because a reader who moved it to see the corner
 * of their estate should not have to move it again on every visit.
 */
const FLOATING_QUEUE_KEY = "marsad.floatingQueue.collapsed";

/** ROUND 15 I6 -- which corner the floating queue takes (pure, tested). */
/** Round 18 G27 -- how high the floating queue sits: above a page's
 *  own bottom bar where it has one. */
export function floatingQueueBottom(
  pathname: string,
): "bottom-24" | "bottom-20" | "bottom-4" {
  if (pathname === "/twin/constellation") return "bottom-24";
  if (pathname.startsWith("/twin/capmap")) return "bottom-20";
  return "bottom-4";
}

export function floatingQueueSide(dir: string, pathname: string): "start-4" | "end-4" {
  return dir === "rtl" && pathname.startsWith("/twin/capmap") ? "end-4" : "start-4";
}

function FloatingQueue({ bottom, children }: { bottom: string; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  // ROUND 15 I6 -- in right-to-left, bottom-START is bottom-RIGHT, which
  // on the capability map is where the scrubber's play control sits: the
  // queue covered it and a real press was refused. The card keeps its
  // corner in English (the constellation's inspector holds bottom-END
  // there) and takes the other corner only where and when it would
  // cover a control.
  const queueSide = floatingQueueSide(i18n.dir(), location.pathname);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(FLOATING_QUEUE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggle = () => {
    setCollapsed((c) => {
      try {
        window.localStorage.setItem(FLOATING_QUEUE_KEY, c ? "0" : "1");
      } catch {
        /* private mode: the toggle still works, it just will not persist. */
      }
      return !c;
    });
  };
  return (
    <div
      data-testid="appshell-floating-queue"
      data-collapsed={collapsed ? "true" : "false"}
      data-side={queueSide}
      className={`fixed ${bottom} ${queueSide} z-40 rounded-lg border border-brand-navyDeep bg-brand-navy/95 px-2 py-1.5 shadow-2xl backdrop-blur`}
    >
      <button
        type="button"
        data-testid="appshell-floating-queue-toggle"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="text-[10px] uppercase tracking-widest text-brand-cream/60 hover:text-brand-cream"
      >
        {collapsed ? t("nav.showQueues") : t("nav.hideQueues")}
      </button>
      {!collapsed && <div className="mt-1">{children}</div>}
    </div>
  );
}
